import type { DashboardId, SubjectStatus } from '@clinspace/shared';
import { SUBJECT_STATUSES } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, bars, fillRoundRect, fitSize, kpiRow, list, text, type Rect } from '../charts.ts';
import { subjectDetail, visitDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

const STATUS_COLOURS: Record<SubjectStatus, string> = {
  Screening: PALETTE.grey,
  'Screen Failed': '#5b6470',
  Enrolled: PALETTE.navy,
  'On Treatment': PALETTE.teal,
  Completed: PALETTE.forest,
  Discontinued: PALETTE.amber,
  'Lost to Follow-up': PALETTE.crimson,
};

/** Subject Status: counts by status, Sankey-style flow, stacked bars per site, drill to timeline. */
export class SubjectStatusDashboard extends Dashboard {
  readonly id: DashboardId = 'SubjectStatus';

  protected override segmentLabel(segment: string): string {
    return segment.replace(/^(status|subject|visit):/, '');
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [status, subject, visit] = this.path;
    if (subject && visit) {
      const v = this.view.visit(visit.slice(6));
      if (v) {
        this.detail = visitDetail(this.view, v);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    if (subject) {
      const s = this.view.subject(subject.slice(8));
      if (!s) return;
      this.detail = subjectDetail(this.view, s);
      const top: Rect = { x: area.x, y: area.y, w: area.w, h: area.h * 0.42 };
      this.drawDetail(
        ctx,
        panel,
        { ...top, h: top.h },
        { ...this.detail, rows: this.detail.rows.slice(0, 6) },
      );
      const visits = this.view.file.visits.filter((v) => v.subjectId === s.id);
      const rows = list(
        ctx,
        ['Visit', 'Target', 'Actual', 'Status', 'Timing'],
        visits.map((v) => ({
          cells: [v.visitName, v.targetDate, v.actualDate ?? '—', v.status, v.timing],
          flag: v.timing === 'Late' || v.status === 'Missed' ? 'x' : undefined,
          colour: v.status === 'Missed' ? PALETTE.crimson : PALETTE.amber,
        })),
        { x: area.x, y: area.y + top.h + 8, w: area.w, h: area.h - top.h - 8 },
        {
          title: 'Subject timeline',
          widths: [0.26, 0.18, 0.18, 0.18, 0.2],
          hoveredIndex: hoveredRow(panel, 'visit:'),
          rowH: 18,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `visit:${i}`,
          ...r,
          data: { kind: 'drill', segment: `visit:${visits[i]!.id}` },
          tooltip: [visits[i]!.visitName, `${visits[i]!.status} · ${visits[i]!.timing}`],
        }),
      );
      return;
    }
    this.detail = undefined;
    const subjects = this.view.subjects(this.filters);
    if (status) {
      const st = status.slice(7) as SubjectStatus;
      const rowsData = subjects.filter((s) => s.status === st);
      const rows = list(
        ctx,
        ['Subject', 'Site', 'Arm', 'Status date', 'Reason'],
        rowsData.map((s) => ({
          cells: [
            s.id,
            this.view.siteLabel(s.siteId),
            s.arm ?? '—',
            s.statusDate,
            s.discontinuationReason ?? s.screenFailReason ?? '—',
          ],
        })),
        area,
        {
          title: `${st} · ${rowsData.length} subjects`,
          widths: [0.16, 0.26, 0.22, 0.16, 0.2],
          hoveredIndex: hoveredRow(panel, 'row:'),
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `subject:${rowsData[i]!.id}` },
          tooltip: [rowsData[i]!.id, 'Open the subject timeline'],
        }),
      );
      return;
    }
    const counts = Object.fromEntries(
      SUBJECT_STATUSES.map((s) => [s, subjects.filter((x) => x.status === s).length]),
    ) as Record<SubjectStatus, number>;
    const shown = SUBJECT_STATUSES.filter((s) => s !== 'Enrolled');
    const tiles = kpiRow(
      ctx,
      shown.map((s) => ({ label: s, value: String(counts[s]), colour: STATUS_COLOURS[s] })),
      { x: area.x, y: area.y, w: area.w, h: 76 },
      hoveredRow(panel, 'kpi:'),
    );
    tiles.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `kpi:${i}`,
        ...r,
        data: { kind: 'drill', segment: `status:${shown[i]}` },
        tooltip: [shown[i]!, `${counts[shown[i]!]} subjects · open the list`],
      }),
    );
    // Flow: screened -> enrolled -> final status as proportional bands.
    const flow: Rect = { x: area.x, y: area.y + 90, w: area.w * 0.5, h: area.h - 90 };
    text(ctx, 'Flow from screening to current status', flow.x, flow.y + 14, {
      size: 12,
      colour: PALETTE.muted,
      weight: 600,
    });
    const total = Math.max(1, subjects.length);
    // Third column leaves room for its labels before the bar chart starts.
    const colX = [flow.x, flow.x + flow.w * 0.28, flow.x + flow.w * 0.64];
    const colW = 36;
    const bandTop = flow.y + 28;
    const bandH = flow.h - 40;
    const drawCol = (
      x: number,
      items: { status: SubjectStatus | 'Screened' | 'Enrolled'; n: number }[],
    ) => {
      let y = bandTop;
      const out: { status: string; y0: number; y1: number }[] = [];
      for (const it of items) {
        const h = (it.n / total) * bandH;
        ctx.fillStyle =
          it.status === 'Screened'
            ? PALETTE.navy
            : (STATUS_COLOURS[it.status as SubjectStatus] ?? PALETTE.navy);
        ctx.fillRect(x, y, colW, Math.max(0, h - 2));
        // Labels stay inside the flow area: the last column's labels end at its right edge.
        if (h > 12) {
          const label = `${it.status} ${it.n}`;
          const room = (colX.find((cx) => cx > x) ?? flow.x + flow.w) - (x + colW + 6) - 4;
          text(ctx, label, x + colW + 6, y + Math.min(h, 14), {
            size: fitSize(ctx, label, room, 9, 7),
            colour: PALETTE.muted,
            maxWidth: room,
          });
        }
        out.push({ status: it.status, y0: y, y1: y + h - 2 });
        y += h;
      }
      return out;
    };
    const enrolledN = subjects.filter((s) => s.randomisationDate).length;
    const c1 = drawCol(colX[0]!, [{ status: 'Screened', n: subjects.length }]);
    const c2 = drawCol(colX[1]!, [
      { status: 'Enrolled', n: enrolledN },
      { status: 'Screening', n: counts.Screening },
      { status: 'Screen Failed', n: counts['Screen Failed'] },
    ]);
    const finals: SubjectStatus[] = [
      'On Treatment',
      'Completed',
      'Discontinued',
      'Lost to Follow-up',
    ];
    const c3 = drawCol(
      colX[2]!,
      finals.map((s) => ({ status: s, n: counts[s] })),
    );
    const band = (
      from: { y0: number; y1: number },
      to: { y0: number; y1: number },
      x0: number,
      x1: number,
      colour: string,
    ) => {
      ctx.beginPath();
      ctx.moveTo(x0, from.y0);
      ctx.bezierCurveTo(x0 + 40, from.y0, x1 - 40, to.y0, x1, to.y0);
      ctx.lineTo(x1, to.y1);
      ctx.bezierCurveTo(x1 - 40, to.y1, x0 + 40, from.y1, x0, from.y1);
      ctx.closePath();
      ctx.fillStyle = colour.replace(')', ', 0.25)').replace('rgb(', 'rgba(');
      ctx.fillStyle = `${colour}55`;
      ctx.fill();
    };
    let acc = c1[0]!.y0;
    for (const seg of c2) {
      const h = seg.y1 - seg.y0;
      band(
        { y0: acc, y1: acc + h },
        seg,
        colX[0]! + colW,
        colX[1]!,
        STATUS_COLOURS[seg.status as SubjectStatus] ?? PALETTE.navy,
      );
      acc += h + 2;
    }
    const enrolledSeg = c2[0]!;
    acc = enrolledSeg.y0;
    for (const seg of c3) {
      const h = seg.y1 - seg.y0;
      band(
        { y0: acc, y1: acc + h },
        seg,
        colX[1]! + colW,
        colX[2]!,
        STATUS_COLOURS[seg.status as SubjectStatus]!,
      );
      acc += h + 2;
    }
    // Stacked bars per site.
    const right: Rect = {
      x: area.x + area.w * 0.54,
      y: area.y + 90,
      w: area.w * 0.46,
      h: area.h - 90,
    };
    const perSite = this.view.file.sites.map((site) => ({
      label: site.city,
      values: shown.map(
        (s) => subjects.filter((x) => x.siteId === site.id && x.status === s).length,
      ),
      colours: shown.map((s) => STATUS_COLOURS[s]),
    }));
    const rects = bars(ctx, perSite, right, {
      stacked: true,
      title: 'Status per site',
      hoveredIndex: hoveredRow(panel, 'site:'),
    });
    rects.forEach((r, i) => {
      const site = this.view.file.sites[i]!;
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [
          this.view.siteLabel(site.id),
          ...shown
            .filter((s) => perSite[i]!.values[shown.indexOf(s)]! > 0)
            .map((s) => `${s}: ${perSite[i]!.values[shown.indexOf(s)]}`),
        ],
        selectable: false,
      });
    });
    fillRoundRect(ctx, { x: area.x, y: area.y + area.h - 2, w: 1, h: 1 }, 0, 'transparent');
  }
}
